import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSchedulerSidebar } from '@contexts/scheduler/sidebar';
import { useAccess } from '@hooks/access';
import { PhoneSection } from '../section';
import { ResourcePicker } from '../resourcePicker';
import { ResourceRow } from '../resourceRow';
import { PhoneResourcesProps } from '../crew/types';
import { useEquipmentOptions } from './useEquipmentOptions';
import { assetLabel } from './helpers';

export const PhoneEquipment = ({
  open,
  onOpenChange,
  onSwap,
  multi,
  specialty,
}: PhoneResourcesProps) => {
  const { t } = useTranslation('app');
  const access = useAccess();
  const [collapsed, setCollapsed] = useState(false);
  const { equipmentMembers, disabled, setState, conflictsByAssetId } = useSchedulerSidebar();
  const readOnly = access.readOnly || disabled;
  const {
    options,
    selected,
    toggle,
    multiConflicts,
    disabled: assetsDisabled,
  } = useEquipmentOptions({ multi, specialty });

  const remove = (assetId: string) =>
    setState((prev) => ({
      ...prev,
      defaultEquipment: false,
      equipmentMembers: prev.equipmentMembers.filter((el) => el.assetId !== assetId),
    }));

  return (
    <PhoneSection
      title={t('schedule.equipment.title', { count: equipmentMembers.length })}
      collapsed={collapsed}
      onCollapsedChange={setCollapsed}>
      {!collapsed && (
        <div className="flex flex-col gap-1.5">
          {!access.readOnly && (
            <ResourcePicker
              open={open}
              onOpenChange={onOpenChange}
              options={options}
              selected={selected}
              onToggle={toggle}
              addLabel={t('schedule.equipment.add')}
              searchPlaceholder={t('schedule.equipment.searchPlaceholder')}
              hint={t('schedule.equipment.updateHint')}
              conflictTitle={t('schedule.equipment.tooltip.conflict.title')}
              disabled={readOnly || assetsDisabled}
            />
          )}
          {equipmentMembers.map((el) => (
            <ResourceRow
              key={el.assetId}
              name={el.name}
              role={el.internalId ? `#${el.internalId}` : ''}
              conflicts={
                el.equipmentDeactivated || multi ? [] : (conflictsByAssetId[el.assetId] ?? [])
              }
              {...multiConflicts(el.assetId, assetLabel(el.name, el.internalId))}
              deactivated={el.equipmentDeactivated}
              disabled={readOnly}
              swapLabel={t('schedule.equipment.tooltip.replace')}
              removeLabel={t('schedule.equipment.tooltip.remove')}
              deactivatedLabel={t('scheduling.phone.deactivated')}
              onSwap={() => onSwap(el.assetId, assetLabel(el.name, el.internalId))}
              onRemove={() => remove(el.assetId)}
            />
          ))}
        </div>
      )}
    </PhoneSection>
  );
};
